/* eslint-disable complexity */
import type {
  ButtonShape,
  DropdownPlacement,
  Placement
} from '@eggplant/types'
import {
  type ReactNode,
  forwardRef
} from 'react'
import {
  HiChevronDown,
  HiChevronRight,
  HiChevronUp
} from 'react-icons/hi'
import { Tooltip } from '../..'
import styles from './DropdownButton.module.scss'

export interface IconButtonShape extends ButtonShape {
  Icon?: ({ size }: {size: string}) => ReactNode
  isSolidIcon?: boolean
  isDisabled?: boolean
  isFocus?: boolean
  isHighlighted?: boolean
  isInvalid?: boolean
  label?: string
  isFilter?: boolean
  isFilterFixedMinMaxWidth?: boolean
  title?: string
  placement?: Placement
  dropdownPlacement?: DropdownPlacement
  isWarningState?: boolean
}

const DropdownButton = forwardRef((
  {
    'aria-haspopup': ariaHasPopup,
    'aria-label': ariaLabel,
    children,
    id,
    isSolidIcon,
    isDisabled,
    onBlur,
    onClick,
    onKeyDown,
    onKeyUp,
    Icon,
    isFocus = false,
    isHighlighted = false,
    isInvalid = false,
    label,
    isFilter = false,
    isFilterFixedMinMaxWidth = true,
    title,
    placement,
    dropdownPlacement = 'bottom',
    isWarningState = false
  }: IconButtonShape,
  ref: any
) => {
  const getHighlightedStyle =
  isFilter && isHighlighted
    ? styles.isHighlighted
    : ''

  const isInvalidStyle = isInvalid
    ? styles.isInvalid
    : ''

  const isBondedLeftStyle = placement === 'left'
    ? styles.isLeftBondedFilters
    : ''

  const isBondedRightStyle = placement === 'right'
    ? styles.isRightBondedFilters
    : ''

  const isBondedTopStyle = placement === 'top'
    ? styles.isTopBondedFilters
    : ''

  const isBondedBottomStyle = placement === 'bottom'
    ? styles.isBottomBondedFilters
    : ''

  const hasControlledWidthStyle = isFilter && isFilterFixedMinMaxWidth
    ? styles.FilterDropdownMinMaxWidth
    : ''

  const getButtonSyles = () => Icon
    ? styles.DropdownButtonIcon
    : styles.DropdownButtonNoIcon

  const buttonClass = [
    getHighlightedStyle,
    isInvalidStyle,
    hasControlledWidthStyle,
    isBondedLeftStyle,
    isBondedRightStyle,
    isBondedTopStyle,
    isBondedBottomStyle,
    getButtonSyles()
  ].filter(style => style).join(' ')

  const downIconDisableStyle = isDisabled
    ? styles.disabled
    : ''

  const downIconClass = [
    styles.downIconWrapper,
    downIconDisableStyle
  ].filter(style => style).join(' ')

  const iconStyle = isSolidIcon
    ? styles.filledModelIconWrapper
    : styles.modelIconWrapper

  const iconStyles = isWarningState
    ? styles.errorIcon
    : iconStyle

  const arrowLogoProps = {
    'aria-hidden': true,
    size: 17
  } // Needed to avoid eslint error

  const renderArrowLogo = () => ariaLabel === 'close menu' || isFocus
    ? <HiChevronUp {...arrowLogoProps} />
    : <HiChevronDown {...arrowLogoProps} />

  const dropdownButtonContent = (
    <button
      aria-haspopup={ariaHasPopup}
      aria-label={ariaLabel}
      aria-labelledby={label} // This is added to handle labeling of the button which used in cypress test, as title is change to handle tooltip, and aria-label is used by downshift.
      className={buttonClass}
      disabled={isDisabled}
      id={id}
      onBlur={onBlur}
      onClick={onClick}
      onKeyDown={onKeyDown}
      onKeyUp={onKeyUp}
      ref={ref}
      type='button'
    >
      {
        Icon &&
          <span
            aria-hidden
            className={iconStyles}
          >
            <Icon size='20px' />
          </span>
      }
      <span className={styles.Children}>
        {children}
      </span>
      <span
        aria-hidden
        className={downIconClass}
      >
        {
          dropdownPlacement === 'bottom'
            ? renderArrowLogo()
            : <HiChevronRight {...arrowLogoProps} />
        }
      </span>
    </button>
  )

  return title
    ? (
      <Tooltip label={title}>
        {dropdownButtonContent}
      </Tooltip>
    )
    : dropdownButtonContent
}
)

export default DropdownButton
